
// Enhanced Toaster component with improved design and English support
// Modern alternative to Shadcn UI components

import React, { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { X, CheckCircle, AlertCircle, XCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Toaster() {
  const { toasts, dismiss } = useToast();

  // Mostra solo i toast di errore
  const filteredToasts = toasts.filter(toast => 
    toast.variant === 'destructive'
  );

  return (
    <div className="fixed top-[90px] z-[100] flex flex-col items-center w-full p-4 md:items-end md:p-6 pointer-events-none">
      <div className="flex flex-col-reverse w-full gap-3 md:w-auto">
        {filteredToasts.map((toast) => (
          <Toast
            key={toast.id}
            toast={toast as any}
            onClose={() => dismiss(toast.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface ToastProps {
  toast: {
    id: string;
    title?: React.ReactNode;
    description?: React.ReactNode;
    duration?: number;
    variant?: "default" | "destructive" | "success" | "warning" | "info";
    [key: string]: any;
  };
  onClose: () => void;
}

function Toast({ toast, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);
    
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    if (toast.duration !== Infinity) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => {
          onClose();
        }, 300);
      }, toast.duration || 2000); // Riduciamo anche la durata
      
      return () => clearTimeout(timer);
    }
  }, [toast.duration, onClose]);
  
  const getVariantStyles = () => {
    switch (toast.variant) {
      case 'success':
        return {
          bg: 'bg-green-500/20 border-green-500/30',
          icon: <CheckCircle className="h-5 w-5 text-green-400" />,
          text: 'text-green-100'
        };
      case 'destructive':
        return {
          bg: 'bg-red-800/70 border-red-700/60',
          icon: <XCircle className="h-5 w-5 text-white" />,
          text: 'text-white'
        };
      case 'warning':
        return {
          bg: 'bg-orange-500/20 border-orange-500/30',
          icon: <AlertCircle className="h-5 w-5 text-orange-400" />,
          text: 'text-orange-100'
        };
      case 'info':
        return {
          bg: 'bg-blue-500/20 border-blue-500/30',
          icon: <Info className="h-5 w-5 text-blue-400" />,
          text: 'text-blue-100'
        };
      default:
        return {
          bg: 'bg-gray-800/90 border-gray-700/50',
          icon: <Info className="h-5 w-5 text-gray-400" />,
          text: 'text-gray-100'
        };
    }
  };
  
  const variantStyle = getVariantStyles();
  
  // Non renderizzare nulla se il toast non è visibile
  if (!isVisible && toast.open === false) {
    return null;
  }

  return (
    <div
      className={cn(
        'backdrop-blur-sm p-4 mb-2 min-w-[320px] max-w-[400px] shadow-xl rounded-xl border pointer-events-auto transition-all transform duration-300 ease-in-out',
        variantStyle.bg,
        variantStyle.text,
        isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-2 opacity-0 scale-95'
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {variantStyle.icon}
        </div>
        <div className="flex-1 min-w-0">
          {toast.title && (
            <div className="font-semibold text-sm leading-tight mb-1">
              {toast.title}
            </div>
          )}
          {toast.description && (
            <div className="text-sm opacity-90 leading-relaxed">
              {toast.description}
            </div>
          )}
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-white/10"
        >
          <X size={16} />
          <span className="sr-only">Close notification</span>
        </button>
      </div>
    </div>
  );
}
